import { useState } from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Select,
  TextInput,
  Tabs,
  Text,
  Divider,
  Badge,
  Tooltip,
  Box,
  MultiSelect
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useAuthInfo } from '@propelauth/react';
import { useUserSearch } from '../../hooks/useUserSearch';
import { env } from '../../utils/env';
import alertCircleIcon from '../../assets/icons/alert-circle.svg';
import userIcon from '../../assets/icons/user.svg';
import emailIcon from '../../assets/icons/email.svg';
import adminIcon from '../../assets/icons/admin.svg';

interface User {
  id: string;
  display_name: string;
  email: string;
  username?: string;
  avatar_url?: string;
  status?: 'active' | 'pending' | 'ghost';
  role?: 'owner' | 'admin' | 'member';
  joined_at?: string;
}

interface InviteMemberModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: number;
  accessToken: string;
  ghostCount?: number;
  ghostLimit?: number;
}

interface EmailInviteFormValues {
  email: string;
  role: string;
}

interface GhostMemberFormValues {
  displayName: string;
  email: string; // Optional for ghost
}

interface AppUserFormValues {
  userIds: string[];
  role: string;
}

const ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

export const InviteMemberModal = ({
  opened,
  onClose,
  onSuccess,
  groupId,
  accessToken,
  ghostCount = 0,
  ghostLimit = 10
}: InviteMemberModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('email');
  const { searchUsers, results: userResults, loading: searchLoading } = useUserSearch(accessToken);
  const [searchQuery, setSearchQuery] = useState('');

  // Form for email invitation
  const emailForm = useForm<EmailInviteFormValues>({
    initialValues: {
      email: '',
      role: 'member',
    },
    validate: {
      email: (value) => (!value.includes('@') ? 'Valid email is required' : null),
      role: (value) => (!value ? 'Role is required' : null),
    },
  });

  // Form for ghost member
  const ghostForm = useForm<GhostMemberFormValues>({
    initialValues: {
      displayName: '',
      email: '', // Optional
    },
    validate: {
      displayName: (value) => (!value ? 'Name is required' : null),
    },
  });

  // Form for existing app users
  const appUserForm = useForm<AppUserFormValues>({
    initialValues: {
      userIds: [],
      role: 'member',
    },
    validate: {
      userIds: (value) => (value.length === 0 ? 'Select at least one user' : null),
      role: (value) => (!value ? 'Role is required' : null),
    },
  });

  const handleInviteByEmail = async (values: EmailInviteFormValues) => {
    try {
      setIsSubmitting(true);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          role: values.role,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to invite member');
      }

      const data = await response.json();
      const isDirectAdd = data.status === 'active';

      notifications.show({
        title: 'Success',
        message: isDirectAdd
          ? 'User added to the group successfully'
          : 'Invitation sent successfully',
        color: 'green',
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error inviting member:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to send invitation',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddGhost = async (values: GhostMemberFormValues) => {
    try {
      setIsSubmitting(true);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members/ghost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: values.displayName,
          email: values.email || undefined, // Only send if provided
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add ghost member');
      }

      notifications.show({
        title: 'Success',
        message: 'Ghost member added successfully',
        color: 'green',
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error adding ghost member:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add ghost member',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInviteAppUsers = async (values: AppUserFormValues) => {
    try {
      setIsSubmitting(true);

      // Make separate requests for each user
      const promises = values.userIds.map(userId => {
        const user = userResults.find(u => u.id === userId);
        if (!user) return null;

        return fetch(`${env.API_URL}/api/groups/${groupId}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            role: values.role,
          }),
        });
      });

      await Promise.all(promises.filter(Boolean));

      notifications.show({
        title: 'Success',
        message: 'App users invited successfully',
        color: 'green',
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error inviting app users:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to invite app users',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    emailForm.reset();
    ghostForm.reset();
    appUserForm.reset();
    setActiveTab('email');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Members"
      size="md"
    >
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List grow>
          <Tabs.Tab value="email" leftSection={<img src={emailIcon} alt="" width={16} />}>
            Invite by Email
          </Tabs.Tab>
          <Tabs.Tab value="ghost" leftSection={<img src={userIcon} alt="" width={16} />}>
            Add Ghost
          </Tabs.Tab>
          <Tabs.Tab value="app-users" leftSection={<img src={adminIcon} alt="" width={16} />}>
            App Users
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="email" pt="md">
          <Stack>
            <Text size="sm">
              Send an email invitation to join this group. A temporary ghost member will be created until they accept.
              If the email belongs to an existing user, they will be added to the group directly.
            </Text>

            <form onSubmit={emailForm.onSubmit(handleInviteByEmail)}>
              <Stack>
                <TextInput
                  label="Email Address"
                  placeholder="email@example.com"
                  withAsterisk
                  {...emailForm.getInputProps('email')}
                />

                <Select
                  label="Role"
                  placeholder="Select role"
                  data={ROLES}
                  {...emailForm.getInputProps('role')}
                  withAsterisk
                />

                <Group justify="flex-end" mt="md">
                  <Button variant="subtle" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={isSubmitting}>
                    Add Member
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="ghost" pt="md">
          <Stack>
            <Group>
              <Text size="sm">
                Ghost members are placeholders for people who don't use the app. They can have expenses split with them but won't receive notifications.
              </Text>

              <Tooltip
                label={`You can have up to ${ghostLimit} ghost members with your current plan.`}
                position="top"
                multiline
                w={220}
              >
                <Badge color={ghostCount >= ghostLimit ? "red" : "gray"}>
                  {ghostCount}/{ghostLimit} ghosts
                  <img src={alertCircleIcon} alt="Info" width={12} style={{ marginLeft: 4 }} />
                </Badge>
              </Tooltip>
            </Group>

            <form onSubmit={ghostForm.onSubmit(handleAddGhost)}>
              <Stack>
                <TextInput
                  label="Ghost Name"
                  placeholder="e.g. John's Partner"
                  withAsterisk
                  disabled={ghostCount >= ghostLimit}
                  {...ghostForm.getInputProps('displayName')}
                />

                <TextInput
                  label="Email Address (optional)"
                  placeholder="email@example.com"
                  description="Adding an email is helpful if they might join later"
                  disabled={ghostCount >= ghostLimit}
                  {...ghostForm.getInputProps('email')}
                />

                <Group justify="flex-end" mt="md">
                  <Button variant="subtle" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    disabled={ghostCount >= ghostLimit}
                  >
                    Add Ghost Member
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="app-users" pt="md">
          <Stack>
            <Text size="sm">
              Add existing users from the app to this group. They will be added directly without needing to accept an invitation.
            </Text>

            <form onSubmit={appUserForm.onSubmit(handleInviteAppUsers)}>
              <Stack>
                <MultiSelect
                  label="Search Users"
                  placeholder="Search by name, username, or email"
                  data={userResults.map(user => {
                    let base = (user.display_name || "").trim();
                    const uname = (user.username || "").trim();
                    if (!base && !uname) {
                      base = `User-${user.id.slice(0, 6)}`;
                    } else if (!base) {
                      base = uname;
                    }
                    const label = uname && base !== uname ? `${base} (@${uname})` : base;
                    return { value: user.id.toString(), label };
                  })}
                  searchable
                  onSearchChange={(query) => { setSearchQuery(query); searchUsers(query); }}
                  searchValue={searchQuery}
                  onDropdownOpen={() => setSearchQuery('')}
                  nothingFoundMessage={searchLoading ? "Searching..." : "No users found"}
                  maxDropdownHeight={280}
                  {...appUserForm.getInputProps('userIds')}
                />

                <Select
                  label="Role"
                  placeholder="Select role"
                  data={ROLES}
                  {...appUserForm.getInputProps('role')}
                  withAsterisk
                />

                <Group justify="flex-end" mt="md">
                  <Button variant="subtle" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={isSubmitting}>
                    Add Members
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}; 